import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Statistic, Row, Col, Spin, message, Tabs, Descriptions, Badge, Empty, Typography, List, Drawer, Button,
} from 'antd';
import {
  UserOutlined, FileTextOutlined, TeamOutlined, PieChartOutlined,
} from '@ant-design/icons';
import { SurveyRecord, DIMENSION_COLORS, DIMENSION_NAMES } from '../types';
import * as api from '../api';
import HappinessWheel from '../components/HappinessWheel';

const { Text } = Typography;

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<Record<string, any>>({});
  const [allSurveys, setAllSurveys] = useState<SurveyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<{ user: any; records: SurveyRecord[] } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [usersData, surveysData] = await Promise.all([
        api.adminGetUsers(),
        api.adminGetAllSurveys(),
      ]);
      setUsers(usersData.users);
      setStats(usersData.stats);
      setAllSurveys(surveysData);
    } catch (err: any) {
      message.error('加载管理数据失败: ' + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const openUserDetail = async (userId: string) => {
    try {
      const data = await api.adminGetUserSurveys(userId);
      setSelectedUser(data);
      setDrawerOpen(true);
    } catch {
      message.error('加载用户详情失败');
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const totalSurveys = allSurveys.length;
  const totalUsers = users.length;
  const avgScore = totalSurveys > 0
    ? Math.round(allSurveys.reduce((s, r) => s + r.totalScore, 0) / totalSurveys)
    : 0;

  // 用户列表表格列
  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (name: string, record: any) => (
        <Button type="link" onClick={() => openUserDetail(record.id)} style={{ padding: 0 }}>
          <UserOutlined style={{ marginRight: 6 }} />
          {name}
        </Button>
      ),
    },
    {
      title: '昵称',
      dataIndex: 'displayName',
      key: 'displayName',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>{role === 'admin' ? '管理员' : '用户'}</Tag>
      ),
    },
    {
      title: '问卷数',
      key: 'surveyCount',
      render: (_: any, record: any) => stats[record.id]?.total || 0,
    },
    {
      title: '平均分',
      key: 'avgScore',
      render: (_: any, record: any) => {
        const s = stats[record.id];
        return s ? <Badge count={s.avg} showZero color="#2ECC71" overflowCount={90} style={{ backgroundColor: '#2ECC71' }} /> : '-';
      },
    },
    {
      title: '最新填写',
      key: 'latest',
      render: (_: any, record: any) => stats[record.id]?.latest || '-',
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: string) => d?.slice(0, 10),
    },
  ];

  // 全部问卷表格列
  const surveyColumns = [
    { title: '日期', dataIndex: 'surveyDate', key: 'date', width: 110 },
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      ellipsis: true,
      render: (id: string) => {
        const u = users.find(u => u.id === id);
        return <span title={id}>{u?.username || id.slice(0, 8) + '...'}</span>;
      },
    },
    {
      title: '总分',
      dataIndex: 'totalScore',
      key: 'totalScore',
      sorter: (a: any, b: any) => a.totalScore - b.totalScore,
      render: (s: number) => {
        const color = s >= 72 ? '#2ECC71' : s >= 54 ? '#F39C12' : '#E91E63';
        return <span style={{ color, fontWeight: 600 }}>{s}</span>;
      },
    },
    ...['身体健康', '心理健康', '关系健康', '事业发展', '财富自由', '价值意义'].map((name, i) => {
      const code = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'][i];
      return {
        title: name,
        key: code,
        width: 80,
        render: (_: any, record: SurveyRecord) => {
          const score = record.dimensionScores[code];
          const color = DIMENSION_COLORS[code];
          return <Tag color={color} style={{ minWidth: 30, textAlign: 'center' }}>{score}</Tag>;
        },
      };
    }),
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 90,
      render: (d: string) => d?.slice(11, 19),
    },
  ];

  return (
    <div>
      {/* 概览统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card hoverable onClick={() => openUserDetail('')} style={{ cursor: 'default' }}>
            <Statistic title="总用户数" value={totalUsers} prefix={<TeamOutlined />} valueStyle={{ color: '#3498DB' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="问卷总数" value={totalSurveys} prefix={<FileTextOutlined />} valueStyle={{ color: '#2ECC71' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="平均分" value={avgScore} suffix="/ 90" prefix={<PieChartOutlined />} valueStyle={{ color: '#F39C12' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="活跃用户（有记录）"
              value={Object.keys(stats).length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#9B59B6' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tab 切换 */}
      <Card>
        <Tabs
          defaultActiveKey="users"
          items={[
            {
              key: 'users',
              label: <span><TeamOutlined /> 用户管理</span>,
              children: (
                <Table
                  dataSource={users}
                  columns={userColumns}
                  rowKey="id"
                  pagination={{ pageSize: 10, showTotal: (t) => `共 ${t} 个用户` }}
                  size="small"
                />
              ),
            },
            {
              key: 'surveys',
              label: <span><FileTextOutlined /> 全部问卷</span>,
              children: (
                <Table
                  dataSource={allSurveys}
                  columns={surveyColumns}
                  rowKey="id"
                  pagination={{ pageSize: 15, showTotal: (t) => `共 ${t} 条问卷` }}
                  size="small"
                  scroll={{ x: 900 }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* 用户详情抽屉 */}
      <Drawer
        title={selectedUser ? `用户: ${selectedUser.user?.displayName || selectedUser.user?.username}` : ''}
        placement="right"
        width={640}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {selectedUser ? (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="用户名">{selectedUser.user?.username}</Descriptions.Item>
              <Descriptions.Item label="昵称">{selectedUser.user?.displayName}</Descriptions.Item>
              <Descriptions.Item label="角色">
                <Tag color={selectedUser.user?.role === 'admin' ? 'red' : 'blue'}>
                  {selectedUser.user?.role === 'admin' ? '管理员' : '普通用户'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="注册时间">{selectedUser.user?.createdAt?.slice(0, 10)}</Descriptions.Item>
              <Descriptions.Item label="问卷数">{selectedUser.records.length}</Descriptions.Item>
            </Descriptions>

            {selectedUser.records.length > 0 ? (
              <div style={{ height: 300, marginBottom: 16 }}>
                <Text strong>幸福之轮趋势</Text>
                <HappinessWheel records={selectedUser.records.slice(0, 6)} />
              </div>
            ) : null}

            <List
              header={<Text strong>问卷记录</Text>}
              dataSource={selectedUser.records}
              renderItem={(record) => (
                <List.Item key={record.id}>
                  <List.Item.Meta
                    title={
                      <span>
                        <Tag>{record.surveyDate}</Tag>
                        总分: <Text strong style={{ color: record.totalScore >= 72 ? '#2ECC71' : '#F39C12' }}>{record.totalScore}</Text>
                      </span>
                    }
                    description={
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {Object.entries(record.dimensionScores).map(([code, score]) => (
                          <Tag key={code} color={DIMENSION_COLORS[code]} style={{ fontSize: 11 }}>
                            {DIMENSION_NAMES[code]}: {score}
                          </Tag>
                        ))}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        ) : (
          <Empty description="请从用户列表中选择一个用户" />
        )}
      </Drawer>
    </div>
  );
};

export default AdminPage;
